import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Trophy, 
  Users, 
  Calendar,
  MapPin,
  Coins,
  Crown,
  Medal,
  Plus,
  ChevronRight,
  Shield,
  Target,
  Award,
  Edit,
  Trash2,
  Play,
  CheckCircle,
  ArrowLeft
} from "lucide-react";

const AdminTournamentsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  
  // Turnuva oluşturma dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // Turnuva düzenleme dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  // Silme onay dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingTournament, setDeletingTournament] = useState(null);
  
  const [createData, setCreateData] = useState({
    name: "",
    description: "",
    type: "cup",
    maxTeams: 8,
    minTeams: 4,
    entryFee: 50,
    startDate: "",
    registrationDeadline: "",
    city: "",
    district: "",
    rewards: {
      first: { popularity: 50, title: "🏆 Şampiyon" },
      second: { popularity: 30, title: "" },
      third: { popularity: 20, title: "" }
    }
  });

  useEffect(() => {
    // Admin kontrolü
    if (!user?.isAdmin) {
      toast.error("Bu sayfaya erişim yetkiniz yok");
      navigate("/");
      return;
    }
    fetchTournaments();
  }, [user, navigate]);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`);
      setTournaments(response.data || []);
    } catch (error) {
      console.error("Turnuvalar yüklenemedi:", error);
      toast.error("Turnuvalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTournament = async () => {
    if (!createData.name.trim()) {
      toast.error("Turnuva adı gerekli");
      return;
    }
    if (!createData.startDate) {
      toast.error("Başlangıç tarihi gerekli");
      return;
    }

    try {
      const response = await axios.post(`${API}/tournaments`, createData);
      
      if (response.data.success) {
        toast.success("Turnuva oluşturuldu!");
        setShowCreateDialog(false);
        setCreateData({
          name: "",
          description: "",
          type: "cup",
          maxTeams: 8,
          minTeams: 4,
          entryFee: 50,
          startDate: "",
          registrationDeadline: "",
          city: "",
          district: "",
          rewards: {
            first: { popularity: 50, title: "🏆 Şampiyon" },
            second: { popularity: 30, title: "" },
            third: { popularity: 20, title: "" }
          }
        });
        fetchTournaments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Turnuva oluşturulamadı");
    }
  };

  const handleEditClick = (tournament) => {
    setEditingTournament(tournament);
    setCreateData({
      name: tournament.name || "",
      description: tournament.description || "",
      type: tournament.type || "cup",
      maxTeams: tournament.maxTeams || 8,
      minTeams: tournament.minTeams || 4,
      entryFee: tournament.entryFee || 50,
      startDate: tournament.startDate?.split('T')[0] || "",
      registrationDeadline: tournament.registrationDeadline?.split('T')[0] || "",
      city: tournament.city || "",
      district: tournament.district || "",
      rewards: tournament.rewards || {
        first: { popularity: 50, title: "🏆 Şampiyon" },
        second: { popularity: 30, title: "" },
        third: { popularity: 20, title: "" }
      }
    });
    setShowEditDialog(true);
  };

  const handleUpdateTournament = async () => {
    if (!createData.name.trim()) {
      toast.error("Turnuva adı gerekli");
      return;
    }

    try {
      const response = await axios.put(`${API}/tournaments/${editingTournament.id}`, createData);
      
      if (response.data.success) {
        toast.success("Turnuva güncellendi!");
        setShowEditDialog(false);
        setEditingTournament(null);
        fetchTournaments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Turnuva güncellenemedi");
    }
  };

  const handleDeleteClick = (tournament) => {
    setDeletingTournament(tournament);
    setShowDeleteDialog(true);
  };

  const handleDeleteTournament = async () => {
    try {
      const response = await axios.delete(`${API}/tournaments/${deletingTournament.id}`);
      
      if (response.data.success) {
        toast.success("Turnuva silindi!");
        setShowDeleteDialog(false);
        setDeletingTournament(null);
        fetchTournaments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Turnuva silinemedi");
    }
  };

  const handleStartTournament = async (tournamentId) => {
    try {
      const response = await axios.post(`${API}/tournaments/${tournamentId}/start`);
      if (response.data.success) {
        toast.success("Turnuva başlatıldı!");
        fetchTournaments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Turnuva başlatılamadı");
    }
  };

  const handleCompleteTournament = async (tournamentId) => {
    try {
      const response = await axios.post(`${API}/tournaments/${tournamentId}/complete`);
      if (response.data.success) {
        toast.success("Turnuva tamamlandı! Ödüller dağıtıldı.");
        fetchTournaments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Turnuva tamamlanamadı");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "registration":
        return <Badge className="bg-green-500">Kayıt Açık</Badge>;
      case "ongoing":
        return <Badge className="bg-blue-500">Devam Ediyor</Badge>;
      case "completed":
        return <Badge className="bg-gray-500">Tamamlandı</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "cup":
        return <Badge variant="outline" className="border-amber-500 text-amber-600">🏆 Kupa</Badge>;
      case "league":
        return <Badge variant="outline" className="border-blue-500 text-blue-600">📊 Lig</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const filteredTournaments = activeTab === "all" 
    ? tournaments 
    : tournaments.filter(t => t.status === activeTab);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20"
                onClick={() => navigate("/admin")}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
                  <Trophy className="h-8 w-8" />
                  Turnuva Yönetimi
                </h1>
                <p className="text-purple-100 mt-1">
                  Turnuva oluştur ve yönet
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-white text-purple-700 hover:bg-purple-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni Turnuva
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Target className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tournaments.filter(t => t.status === "registration").length}</p>
                  <p className="text-xs text-gray-500">Kayıt Açık</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Play className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tournaments.filter(t => t.status === "ongoing").length}</p>
                  <p className="text-xs text-gray-500">Devam Eden</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tournaments.filter(t => t.status === "completed").length}</p>
                  <p className="text-xs text-gray-500">Tamamlanan</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Users className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{tournaments.reduce((sum, t) => sum + (t.teams?.length || 0), 0)}</p>
                  <p className="text-xs text-gray-500">Toplam Takım</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="all">Tümü ({tournaments.length})</TabsTrigger>
            <TabsTrigger value="registration">Kayıt Açık</TabsTrigger>
            <TabsTrigger value="ongoing">Devam Eden</TabsTrigger>
            <TabsTrigger value="completed">Tamamlanan</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {filteredTournaments.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Trophy className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Turnuva bulunamadı</p>
                  <Button 
                    className="mt-4"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    İlk Turnuvayı Oluştur
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredTournaments.map((tournament) => (
                  <Card key={tournament.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold">{tournament.name}</h3>
                            {getTypeBadge(tournament.type)}
                            {getStatusBadge(tournament.status)}
                          </div>
                          <p className="text-sm text-gray-500 mb-3">{tournament.description}</p>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {tournament.teams?.length || 0}/{tournament.maxTeams} takım
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {new Date(tournament.startDate).toLocaleDateString('tr-TR')}
                            </div>
                            <div className="flex items-center gap-1">
                              <Coins className="h-4 w-4 text-emerald-600" />
                              {tournament.entryFee} Kredi
                            </div>
                            {tournament.city && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {tournament.city}
                              </div>
                            )}
                          </div>

                          {/* Ödüller */}
                          <div className="mt-3 flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-1 text-amber-600">
                              <Crown className="h-3 w-3" />
                              1.: +{tournament.rewards?.first?.popularity || 50} puan
                            </span>
                            <span className="flex items-center gap-1 text-gray-500">
                              <Medal className="h-3 w-3" />
                              2.: +{tournament.rewards?.second?.popularity || 30} puan
                            </span>
                            <span className="flex items-center gap-1 text-orange-600">
                              <Medal className="h-3 w-3" />
                              3.: +{tournament.rewards?.third?.popularity || 20} puan
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/tournaments/${tournament.id}`)}
                          >
                            Detaylar
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                          
                          {tournament.status === "registration" && (
                            <>
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditClick(tournament)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Düzenle
                              </Button>
                              {(tournament.teams?.length || 0) >= 2 && (
                                <Button 
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => handleStartTournament(tournament.id)}
                                >
                                  <Play className="h-4 w-4 mr-1" />
                                  Başlat
                                </Button>
                              )}
                            </>
                          )}
                          
                          {tournament.status === "ongoing" && (
                            <Button 
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleCompleteTournament(tournament.id)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Bitir
                            </Button>
                          )}
                          
                          {tournament.status !== "ongoing" && (
                            <Button 
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteClick(tournament)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Sil
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Turnuva Oluşturma Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Yeni Turnuva Oluştur
            </DialogTitle>
            <DialogDescription>
              Turnuva bilgilerini doldurun
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Turnuva Adı */}
            <div className="space-y-2">
              <Label>Turnuva Adı *</Label>
              <Input
                value={createData.name}
                onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                placeholder="Örn: Krampon Kış Kupası 2025"
              />
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={createData.description}
                onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                placeholder="Turnuva hakkında kısa bilgi..."
                rows={2}
              />
            </div>

            {/* Tür ve Takım Sayısı */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Turnuva Türü *</Label>
                <Select
                  value={createData.type}
                  onValueChange={(value) => setCreateData({ ...createData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cup">🏆 Kupa (Eleme)</SelectItem>
                    <SelectItem value="league">📊 Lig (Puan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Maksimum Takım *</Label>
                <Select
                  value={createData.maxTeams.toString()}
                  onValueChange={(value) => setCreateData({ ...createData, maxTeams: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">4 Takım</SelectItem>
                    <SelectItem value="8">8 Takım</SelectItem>
                    <SelectItem value="16">16 Takım</SelectItem>
                    <SelectItem value="32">32 Takım</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Minimum Takım */}
            <div className="space-y-2">
              <Label>Minimum Takım Sayısı *</Label>
              <Select
                value={createData.minTeams.toString()}
                onValueChange={(value) => setCreateData({ ...createData, minTeams: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Takım</SelectItem>
                  <SelectItem value="4">4 Takım</SelectItem>
                  <SelectItem value="8">8 Takım</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Turnuva başlaması için en az bu kadar takım gerekli</p>
            </div>

            {/* Katılım Ücreti ve Tarihler */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Katılım Ücreti (Kredi) *</Label>
                <Input
                  type="number"
                  value={createData.entryFee}
                  onChange={(e) => setCreateData({ ...createData, entryFee: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>Son Başvuru Tarihi *</Label>
                <Input
                  type="date"
                  value={createData.registrationDeadline}
                  onChange={(e) => setCreateData({ ...createData, registrationDeadline: e.target.value })}
                />
              </div>
            </div>

            {/* Başlangıç Tarihi */}
            <div className="space-y-2">
              <Label>Başlangıç Tarihi *</Label>
              <Input
                type="date"
                value={createData.startDate}
                onChange={(e) => setCreateData({ ...createData, startDate: e.target.value })}
              />
              <p className="text-xs text-gray-500">Turnuvanın başlayacağı tarih</p>
            </div>

            {/* Şehir ve İlçe */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Şehir</Label>
                <Input
                  value={createData.city}
                  onChange={(e) => setCreateData({ ...createData, city: e.target.value })}
                  placeholder="Örn: İstanbul"
                />
              </div>
              <div className="space-y-2">
                <Label>İlçe</Label>
                <Input
                  value={createData.district}
                  onChange={(e) => setCreateData({ ...createData, district: e.target.value })}
                  placeholder="Örn: Kadıköy"
                />
              </div>
            </div>

            {/* Ödüller */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                Ödüller (Popülerlik Puanı)
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Crown className="h-3 w-3 text-yellow-500" />
                    1. (Şampiyon)
                  </Label>
                  <Input
                    type="number"
                    value={createData.rewards.first.popularity}
                    onChange={(e) => setCreateData({
                      ...createData,
                      rewards: {
                        ...createData.rewards,
                        first: { ...createData.rewards.first, popularity: parseInt(e.target.value) || 0 }
                      }
                    })}
                    placeholder="Puan"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Medal className="h-3 w-3 text-gray-400" />
                    2.
                  </Label>
                  <Input
                    type="number"
                    value={createData.rewards.second.popularity}
                    onChange={(e) => setCreateData({
                      ...createData,
                      rewards: {
                        ...createData.rewards,
                        second: { ...createData.rewards.second, popularity: parseInt(e.target.value) || 0 }
                      }
                    })}
                    placeholder="Puan"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Medal className="h-3 w-3 text-orange-400" />
                    3.
                  </Label>
                  <Input
                    type="number"
                    value={createData.rewards.third.popularity}
                    onChange={(e) => setCreateData({
                      ...createData,
                      rewards: {
                        ...createData.rewards,
                        third: { ...createData.rewards.third, popularity: parseInt(e.target.value) || 0 }
                      }
                    })}
                    placeholder="Puan"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Şampiyon Ünvanı</Label>
                <Input
                  value={createData.rewards.first.title}
                  onChange={(e) => setCreateData({
                    ...createData,
                    rewards: {
                      ...createData.rewards,
                      first: { ...createData.rewards.first, title: e.target.value }
                    }
                  })}
                  placeholder="Örn: 🏆 Şampiyon"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleCreateTournament}>
              <Plus className="h-4 w-4 mr-2" />
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Turnuva Düzenleme Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-500" />
              Turnuva Düzenle
            </DialogTitle>
            <DialogDescription>
              {editingTournament?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label>Turnuva Adı *</Label>
              <Input
                value={createData.name}
                onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={createData.description}
                onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Katılım Ücreti (Kredi)</Label>
                <Input
                  type="number"
                  value={createData.entryFee}
                  onChange={(e) => setCreateData({ ...createData, entryFee: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={createData.startDate}
                  onChange={(e) => setCreateData({ ...createData, startDate: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Şehir</Label>
                <Input
                  value={createData.city}
                  onChange={(e) => setCreateData({ ...createData, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>İlçe</Label>
                <Input
                  value={createData.district}
                  onChange={(e) => setCreateData({ ...createData, district: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Ödüller (Popülerlik Puanı)</Label>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">1. (Şampiyon)</Label>
                  <Input
                    type="number"
                    value={createData.rewards?.first?.popularity || 50}
                    onChange={(e) => setCreateData({
                      ...createData,
                      rewards: {
                        ...createData.rewards,
                        first: { ...createData.rewards?.first, popularity: parseInt(e.target.value) || 0 }
                      }
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">2.</Label>
                  <Input
                    type="number"
                    value={createData.rewards?.second?.popularity || 30}
                    onChange={(e) => setCreateData({
                      ...createData,
                      rewards: {
                        ...createData.rewards,
                        second: { ...createData.rewards?.second, popularity: parseInt(e.target.value) || 0 }
                      }
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">3.</Label>
                  <Input
                    type="number"
                    value={createData.rewards?.third?.popularity || 20}
                    onChange={(e) => setCreateData({
                      ...createData,
                      rewards: {
                        ...createData.rewards,
                        third: { ...createData.rewards?.third, popularity: parseInt(e.target.value) || 0 }
                      }
                    })}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleUpdateTournament}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silme Onay Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Turnuva Sil
            </DialogTitle>
            <DialogDescription>
              "{deletingTournament?.name}" turnuvasını silmek istediğinizden emin misiniz?
              {deletingTournament?.status === "registration" && deletingTournament?.teams?.length > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ Kayıtlı takımlara kredileri iade edilecektir.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteTournament}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTournamentsPage;
